/**
 * Portal API Client
 *
 * Story: 11-1-portal-de-acesso-externo
 * Task: Frontend - Types e API Client
 *
 * SEPARATE Axios instance for portal authentication and data access
 * CRITICAL: Uses separate localStorage keys to avoid conflicts with internal system
 */
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from "axios";
import {
  PortalLoginRequest,
  PortalRefreshRequest,
  PortalAuthResponse,
  PortalRefreshResponse,
  PortalActivateRequest,
  PortalActivateValidateResponse,
  PortalChangePasswordRequest,
  DashboardSummary,
  ProcessosListResponse,
  ProcessosQueryParams,
  FinanceiroListResponse,
  FinanceiroQueryParams,
  DocumentoEquipe,
  DocumentoEquipeListResponse,
  DocumentoEquipeDownload,
  APIError,
} from "@/types/portal";
import { FeedAttachment } from "@/types/feed";
import { soltarDonoDoPush } from "@/lib/push/donoDoPush";

// ============================================================================
// Configuration
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
// Note: API_BASE_URL already includes /api/v1 from VITE_API_URL
const API_PREFIX = "/portal";

// LocalStorage keys - SEPARATE from main system
const STORAGE_KEYS = {
  ACCESS_TOKEN: "portal_access_token",
  REFRESH_TOKEN: "portal_refresh_token",
  USER: "portal_user",
} as const;

// ============================================================================
// Storage Utilities
// ============================================================================

export const portalStorage = {
  getAccessToken: (): string | null => {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  getUser: () => {
    const userData = localStorage.getItem(STORAGE_KEYS.USER);
    return userData ? JSON.parse(userData) : null;
  },

  setTokens: (accessToken: string, refreshToken: string): void => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  },

  setUser: (user: any): void => {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  },

  /**
   * Fim da sessão do portal: o "Sair", o refresh recusado (interceptor), o `irParaOLoginDoPortal` do socket e as falhas do `initAuth` só
   * chamam isto quando a sessão acabou. A marca de dono do push do aparelho sai junto (se for do portal), senão o colaborador do interno
   * no mesmo navegador não toma o push e os avisos do parceiro seguem chegando ali (decisão do Augusto, 12/10/2026).
   */
  clear: (): void => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    soltarDonoDoPush("portal");
  },
};

// ============================================================================
// Refresh Token Queue Management
// ============================================================================

interface RefreshQueueItem {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}

let isRefreshing = false;
let refreshQueue: RefreshQueueItem[] = [];

const processQueue = (error: any, token: string | null = null) => {
  refreshQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token!);
    }
  });

  refreshQueue = [];
};

// ============================================================================
// Axios Instance Creation
// ============================================================================

const createPortalAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Request Interceptor: Add Authorization header
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = portalStorage.getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response Interceptor: Handle 401 with refresh token logic
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      // Handle 401 Unauthorized
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        // If already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshQueue.push({
              resolve: (token: string) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                resolve(instance(originalRequest));
              },
              reject: (err: any) => {
                reject(err);
              },
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = portalStorage.getRefreshToken();

          if (!refreshToken) {
            throw new Error("No refresh token available");
          }

          // Call refresh endpoint
          const response = await axios.post<PortalRefreshResponse>(
            `${API_BASE_URL}/portal/auth/refresh`,
            { refresh_token: refreshToken },
            {
              headers: { "Content-Type": "application/json" },
            }
          );

          const { access_token } = response.data;

          // Update stored token
          localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token);

          // Process queued requests
          processQueue(null, access_token);

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }
          return instance(originalRequest);
        } catch (refreshError) {
          // Refresh failed - clear tokens and redirect to login
          processQueue(refreshError, null);
          portalStorage.clear();

          // Redirect to portal login
          if (window.location.pathname !== "/portal/login") {
            window.location.href = "/portal/login";
          }

          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Create the portal API instance
// Named export (além do default abaixo) porque outros módulos (ex:
// usePushSubscription.ts, Task 6b) precisam do MESMO cliente autenticado do
// portal, não podem recriar a instância. Ver "Files" na task 6b: mudança
// mínima só desta linha — o resto do arquivo é de outra tarefa.
export const portalApi = createPortalAxiosInstance();

// ============================================================================
// API Functions
// ============================================================================

/**
 * Authentication API
 */
export const portalAuthApi = {
  /**
   * Portal login
   * @param credentials - Email and password
   * @returns Auth response with tokens and user info
   */
  login: async (credentials: PortalLoginRequest): Promise<PortalAuthResponse> => {
    const response = await axios.post<PortalAuthResponse>(
      `${API_BASE_URL}/portal/auth/login`,
      credentials,
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    // Store tokens and user in localStorage
    const { access_token, refresh_token, user } = response.data;
    portalStorage.setTokens(access_token, refresh_token);
    portalStorage.setUser(user);

    return response.data;
  },

  /**
   * Validate a first-access (invitation) token — no auth, no side effects.
   * Used by the /portal/primeiro-acesso page on load.
   */
  validateActivation: async (token: string): Promise<PortalActivateValidateResponse> => {
    const response = await axios.get<PortalActivateValidateResponse>(
      `${API_BASE_URL}/portal/auth/activate/validate`,
      { params: { token } }
    );
    return response.data;
  },

  /**
   * Activate a pre-created partner account (first access) and auto-login.
   * On success, persists tokens + user exactly like login.
   * @param payload - token + chosen password (+ confirmation)
   * @returns Auth response with tokens and user info
   */
  activate: async (payload: PortalActivateRequest): Promise<PortalAuthResponse> => {
    const response = await axios.post<PortalAuthResponse>(
      `${API_BASE_URL}/portal/auth/activate`,
      payload,
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    // Auto-login: store tokens and user (same as login)
    const { access_token, refresh_token, user } = response.data;
    portalStorage.setTokens(access_token, refresh_token);
    portalStorage.setUser(user);

    return response.data;
  },

  /**
   * Change the authenticated partner's password (obrigatória no 1º acesso ou
   * voluntária). Usa a instância autenticada (Bearer via interceptor).
   */
  changePassword: async (payload: PortalChangePasswordRequest): Promise<{ message: string }> => {
    const response = await portalApi.post("/portal/auth/change-password", payload);
    return response.data;
  },

  /**
   * Refresh access token
   * @param refreshToken - Refresh token
   * @returns New access token
   */
  refresh: async (refreshToken: string): Promise<PortalRefreshResponse> => {
    const response = await axios.post<PortalRefreshResponse>(
      `${API_BASE_URL}/portal/auth/refresh`,
      { refresh_token: refreshToken } as PortalRefreshRequest,
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    // Update access token in localStorage
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.access_token);

    return response.data;
  },

  /**
   * Logout - clear tokens and user data
   */
  logout: (): void => {
    portalStorage.clear();
  },
};

/**
 * Dashboard API
 */
export const portalDashboardApi = {
  /**
   * Get dashboard summary metrics
   * @returns Dashboard summary with process and financial metrics
   */
  getDashboard: async (): Promise<DashboardSummary> => {
    const response = await portalApi.get<DashboardSummary>(`${API_PREFIX}/dashboard`);
    return response.data;
  },

  /**
   * Get paginated list of processes
   * @param params - Query parameters (page, page_size, status_filter)
   * @returns Paginated process list
   */
  getProcessos: async (params?: ProcessosQueryParams): Promise<ProcessosListResponse> => {
    const response = await portalApi.get<ProcessosListResponse>(`${API_PREFIX}/processos`, {
      params,
    });
    return response.data;
  },

  /**
   * Get paginated list of payments
   * @param params - Query parameters (page, page_size)
   * @returns Paginated payment list
   */
  getFinanceiro: async (params?: FinanceiroQueryParams): Promise<FinanceiroListResponse> => {
    const response = await portalApi.get<FinanceiroListResponse>(`${API_PREFIX}/financeiro`, {
      params,
    });
    return response.data;
  },
};

// ============================================================================
// Central do Parceiro — Feed (E21.1.3)
// ============================================================================

export interface PortalFeedItem {
  id: string;
  post_type: string;
  title: string;
  content_html: string;
  // Mesmo formato do Feed da Empresa (Task 9/10, PR 2) — permite reusar
  // `FeedAttachments` no portal sem duplicar a renderização de vídeo/imagem/arquivo.
  attachments: FeedAttachment[];
  sector_name?: string | null;
  published_at: string;
  expires_at?: string | null;
  // E21.5.2 — Leitura Obrigatória: badge/prazo + se ESTE parceiro já confirmou.
  is_mandatory?: boolean;
  mandatory_deadline?: string | null;
  confirmed?: boolean;
}

export interface PortalFeedResponse {
  items: PortalFeedItem[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * PR 5 — um item obrigatório (post da Central) que ESTE parceiro ainda não
 * confirmou. Espelha `PendingReadResponse` (`app/schemas/central.py`).
 *
 * M9 (revisão final) — `/central/pending-reads` hoje devolve SÓ posts, com
 * `item_type` sempre `"post"` (`CentralReadService.pending_for_user`, que só
 * consulta `CentralPost`/`CentralMandatoryRead`). Documentos obrigatórios
 * pendentes não estão nesta lista. `item_type` fica como `string` (não um
 * union fechado) porque a intenção é linkar pra lugares diferentes se um dia
 * a lista crescer — hoje o Início só usa a CONTAGEM (`AvisoLeiturasPendentes`).
 */
export interface PendingReadItem {
  item_type: string;
  item_id: string;
  title: string;
  deadline?: string | null;
  published_at?: string | null;
}

// E21.3.3 — Biblioteca de Documentos no portal (FR-7)
export interface PortalDocument {
  id: string;
  category: string;
  title: string;
  source: string; // upload | drive_ref
  content_type: string;
  size_bytes: number;
  sector_name?: string | null;
  created_at: string;
  // E21.3.5 — Leitura Obrigatória: badge/prazo + se ESTE parceiro já confirmou.
  is_mandatory?: boolean;
  mandatory_deadline?: string | null;
  confirmed?: boolean;
}

export interface PortalDocumentListResponse {
  items: PortalDocument[];
  total: number;
  page: number;
  page_size: number;
}

export interface DocumentDownloadInfo {
  download_url: string;
  filename: string;
  content_type: string;
}

// E21.3.4 — preview online (FR-8). `url` = proxy same-origin assinado (serve inline).
export interface DocumentPreviewInfo {
  url: string;
  content_type: string;
  filename: string;
  previewable: boolean; // PDF/imagem → preview nativo; senão o front cai no download
}

// E21.2.3 — Base de Conhecimento no portal (FR-15). Item da lista/busca: SEM o corpo.
export interface PortalArticleListItem {
  id: string;
  category: string;
  title: string;
  sector_name?: string | null;
  published_at?: string | null;
  // E21.2.4 — Leitura Obrigatória: badge/prazo + se ESTE parceiro já confirmou.
  is_mandatory?: boolean;
  mandatory_deadline?: string | null;
  confirmed?: boolean;
}

export interface PortalArticleListResponse {
  items: PortalArticleListItem[];
  total: number;
  page: number;
  page_size: number;
}

// Artigo aberto no leitor (com o conteúdo HTML sanitizado no backend).
export interface PortalArticleDetail {
  id: string;
  category: string;
  title: string;
  content_html: string;
  sector_name?: string | null;
  published_at?: string | null;
  // E21.2.4 — Leitura Obrigatória: badge/prazo + se ESTE parceiro já confirmou.
  is_mandatory?: boolean;
  mandatory_deadline?: string | null;
  confirmed?: boolean;
}

// E21.4.3b — self-service de remessa do parceiro
export interface PortalRemessaTemplate {
  id: string;
  name: string;
}

export interface PortalRemessaTerminal {
  id: string;
  label: string;
}

export interface PortalGenerateRemessaPayload {
  terminal_id: string;
  data_remessa: string; // ISO date yyyy-mm-dd
  chamado_number: string;
  tecnico?: string | null;
}

export interface PortalRemessaGeneration {
  id: string;
  template_id: string;
  template_name?: string | null;
  terminal_id: string;
  substabelecido_id: string;
  protocol_number: number;
  data_remessa: string;
  chamado_number: string;
  tecnico?: string | null;
  generated_at: string;
}

export interface PortalRemessaGenerationList {
  items: PortalRemessaGeneration[];
  total: number;
  page: number;
  page_size: number;
}

export const portalCentralApi = {
  /** Feed da Central no portal — só os posts cuja Audiência inclui o parceiro. */
  getFeed: async (params?: { page?: number; page_size?: number }): Promise<PortalFeedResponse> => {
    const response = await portalApi.get<PortalFeedResponse>(`${API_PREFIX}/central/feed`, {
      params,
    });
    return response.data;
  },

  /**
   * PR 4 Task 5 — abre UM post da Central pelo id (o `?post=<id>` da URL,
   * geralmente vindo do link de uma notificação do sininho). Mesma forma de
   * `PortalFeedItem` (anexos já assinados). 404 quando o post não existe mais
   * ou saiu da Audiência do parceiro — o chamador trata como "indisponível".
   */
  getCentralPost: async (postId: string): Promise<PortalFeedItem> => {
    // M2 (revisão final do PR 4) — defesa em profundidade: `usePortalCentralPost`
    // já valida UUID antes de chamar isto, mas encodeURIComponent garante que
    // NENHUM valor vira segmento de path cru (ex.: "../documents/x/download"
    // colapsaria no browser antes mesmo do encode, então isto sozinho não
    // bastaria — é o par da validação no hook, não substituto dela).
    const response = await portalApi.get<PortalFeedItem>(
      `${API_PREFIX}/central/posts/${encodeURIComponent(postId)}`
    );
    return response.data;
  },

  /** E21.5.2 — confirma a leitura de um post de Leitura Obrigatória (idempotente). */
  confirmRead: async (postId: string): Promise<void> => {
    await portalApi.post(`${API_PREFIX}/central/posts/${postId}/confirm-read`, {});
  },

  /**
   * PR 5 — leituras obrigatórias pendentes do parceiro (aviso âmbar do
   * Início). O endpoint já existe desde o G5 (`portal_pending_reads`); só
   * faltava o método no client — nenhuma tela do frontend o chamava ainda.
   */
  getPendingReads: async (): Promise<PendingReadItem[]> => {
    const response = await portalApi.get<PendingReadItem[]>(`${API_PREFIX}/central/pending-reads`);
    return response.data;
  },

  /** E21.3.3 — Biblioteca: documentos da minha Audiência (filtro opcional por categoria). */
  getDocuments: async (params?: {
    page?: number;
    page_size?: number;
    category?: string;
  }): Promise<PortalDocumentListResponse> => {
    const response = await portalApi.get<PortalDocumentListResponse>(
      `${API_PREFIX}/central/documents`,
      { params }
    );
    return response.data;
  },

  /** E21.3.3 — presigned URL (attachment) p/ baixar um documento da minha Audiência. */
  getDownloadInfo: async (docId: string): Promise<DocumentDownloadInfo> => {
    const response = await portalApi.get<DocumentDownloadInfo>(
      `${API_PREFIX}/central/documents/${docId}/download`
    );
    return response.data;
  },

  /** E21.3.4 — info de preview online (PDF/imagem inline; senão front cai no download). */
  getPreviewInfo: async (docId: string): Promise<DocumentPreviewInfo> => {
    const response = await portalApi.get<DocumentPreviewInfo>(
      `${API_PREFIX}/central/documents/${docId}/preview`
    );
    return response.data;
  },

  /** E21.3.5 — confirma a leitura de um documento obrigatório (idempotente). */
  confirmReadDocument: async (docId: string): Promise<void> => {
    await portalApi.post(`${API_PREFIX}/central/documents/${docId}/confirm-read`, {});
  },

  /**
   * E21.2.3 — Base de Conhecimento: artigos PUBLICADOS da minha Audiência.
   * `q` = busca full-text PT-BR (ordena por relevância); `category` = filtro.
   * Campos vazios são omitidos (axios descarta `undefined`).
   */
  getArticles: async (params?: {
    page?: number;
    page_size?: number;
    category?: string;
    q?: string;
  }): Promise<PortalArticleListResponse> => {
    const response = await portalApi.get<PortalArticleListResponse>(
      `${API_PREFIX}/central/articles`,
      { params }
    );
    return response.data;
  },

  /** E21.2.3 — abre um artigo publicado da minha Audiência (com o conteúdo). */
  getArticleDetail: async (articleId: string): Promise<PortalArticleDetail> => {
    const response = await portalApi.get<PortalArticleDetail>(
      `${API_PREFIX}/central/articles/${articleId}`
    );
    return response.data;
  },

  /** E21.2.4 — confirma a leitura de um artigo obrigatório (idempotente). */
  confirmReadArticle: async (articleId: string): Promise<void> => {
    await portalApi.post(`${API_PREFIX}/central/articles/${articleId}/confirm-read`, {});
  },

  // E21.4.3b — self-service de remessa do parceiro (FR-17 no portal)
  listRemessaTemplates: async (): Promise<PortalRemessaTemplate[]> =>
    (await portalApi.get(`${API_PREFIX}/central/remessa-templates`)).data,

  listRemessaTerminals: async (search = ""): Promise<PortalRemessaTerminal[]> =>
    (
      await portalApi.get(`${API_PREFIX}/central/remessa-terminals`, {
        params: { search },
      })
    ).data,

  generateRemessa: async (
    templateId: string,
    payload: PortalGenerateRemessaPayload
  ): Promise<PortalRemessaGeneration> =>
    (await portalApi.post(`${API_PREFIX}/central/templates/${templateId}/generate`, payload)).data,

  listRemessaGenerations: async (page = 1, pageSize = 20): Promise<PortalRemessaGenerationList> =>
    (
      await portalApi.get(`${API_PREFIX}/central/remessa-generations`, {
        params: { page, page_size: pageSize },
      })
    ).data,

  getRemessaDownload: async (generationId: string): Promise<DocumentDownloadInfo> =>
    (await portalApi.get(`${API_PREFIX}/central/remessa-generations/${generationId}/download`))
      .data,
};

// ============================================================================
// Notificações do Portal (E21.1.5) — inbox do Communication Hub p/ o parceiro
// ============================================================================

export interface PortalNotification {
  id: string;
  title: string;
  body: string;
  severity: string;
  module: string | null;
  event_type: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  // PR 4 Task 5 — endereço fechado calculado no backend (`link_da_notificacao`);
  // `null` quando o tipo de evento não tem destino conhecido (lista fechada).
  link?: string | null;
}

export interface PortalNotificationListResponse {
  items: PortalNotification[];
  total: number;
  has_more: boolean;
}

export const portalNotificationsApi = {
  list: async (params?: {
    limit?: number;
    offset?: number;
    only_unread?: boolean;
  }): Promise<PortalNotificationListResponse> => {
    const response = await portalApi.get<PortalNotificationListResponse>(
      `${API_PREFIX}/notifications`,
      { params }
    );
    return response.data;
  },

  unreadCount: async (): Promise<number> => {
    const response = await portalApi.get<{ unread_count: number }>(
      `${API_PREFIX}/notifications/unread-count`
    );
    return response.data.unread_count;
  },

  markRead: async (id: string): Promise<PortalNotification> => {
    const response = await portalApi.post<PortalNotification>(
      `${API_PREFIX}/notifications/${id}/read`
    );
    return response.data;
  },

  markAllRead: async (): Promise<number> => {
    const response = await portalApi.post<{ updated_count: number }>(
      `${API_PREFIX}/notifications/mark-all-read`
    );
    return response.data.updated_count;
  },
};

// ============================================================================
// Error Utilities
// ============================================================================

/**
 * Extract error message from API error response
 */
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as APIError | undefined;
    return apiError?.detail || error.message || "An unexpected error occurred";
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred";
};

/**
 * Pacote D — documentos que o DONO do parceiro compartilha com a equipe dele.
 * `manage` (subir/remover) é só do dono; `view` (listar/baixar) é dos dois.
 */
export const documentosEquipeApi = {
  listar: async (params?: {
    page?: number;
    page_size?: number;
  }): Promise<DocumentoEquipeListResponse> => {
    const response = await portalApi.get<DocumentoEquipeListResponse>(
      `${API_PREFIX}/documentos-equipe`,
      { params }
    );
    return response.data;
  },

  subir: async (dados: {
    titulo: string;
    categoria?: string;
    arquivo: File;
  }): Promise<DocumentoEquipe> => {
    const form = new FormData();
    form.append("titulo", dados.titulo);
    if (dados.categoria) form.append("categoria", dados.categoria);
    form.append("arquivo", dados.arquivo);

    const response = await portalApi.post<DocumentoEquipe>(
      `${API_PREFIX}/documentos-equipe`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  /** URL assinada de vida curta — nunca persistir. */
  obterDownload: async (docId: string): Promise<DocumentoEquipeDownload> => {
    const response = await portalApi.get<DocumentoEquipeDownload>(
      `${API_PREFIX}/documentos-equipe/${docId}/download`
    );
    return response.data;
  },

  remover: async (docId: string): Promise<void> => {
    await portalApi.delete(`${API_PREFIX}/documentos-equipe/${docId}`);
  },
};

// Export default for convenience
export default portalApi;
